import { call, put, takeEvery, all } from 'redux-saga/effects'
import { notification } from 'antd'
import * as api from './api'
import * as actions from './actions'
import { formatArcCampaignForAPI } from './formatters'

function* fetchArcCampaignsWorker({ payload = {} }) {
  try {
    const { page = 1, search = '', filters = {}, sorter = { sort: 'created_at' } } = payload

    yield put(actions.saveFiltersAndSorter({ search, filters, sorter }))

    const response = yield call(api.getArcCampaigns, { page, search, filters, sorter })

    if (response && response.data) {
      const campaigns = response.data.data || []
      const sortedIds = campaigns.map(campaign => campaign.id)
      const included = response.data.included || []

      yield put(actions.fetchArcCampaignsSuccess({
        campaigns,
        sortedIds,
        included,
        page: response.data.meta?.page || 1,
        pageCount: response.data.meta?.page_count || 1,
      }))
    }
  } catch (error) {
    yield put(actions.fetchArcCampaignsFailure())
    notification.error({
      message: 'Error fetching campaigns',
      description: error.message || 'Failed to load ARC campaigns',
    })
  }
}

function* fetchArcCampaignDetailsWorker({ payload }) {
  try {
    const { id } = payload
    const response = yield call(api.getArcCampaign, id)

    if (response && response.data) {
      yield put(actions.fetchArcCampaignDetailsSuccess({
        campaign: response.data.data,
        included: response.data.included || [],
      }))
    }
  } catch (error) {
    yield put(actions.fetchArcCampaignDetailsFailure())
    notification.error({
      message: 'Error fetching campaign details',
      description: error.message || 'Failed to load campaign details',
    })
  }
}

function* createArcCampaignWorker({ payload }) {
  try {
    yield put(actions.startSaving())

    const { data, callback } = payload
    const formattedData = formatArcCampaignForAPI(data)

    const response = yield call(api.createArcCampaign, formattedData)

    if (response && response.data) {
      yield put(actions.createArcCampaignSuccess({ data: response.data.data }))

      notification.success({
        message: 'Campaign created',
        description: 'ARC campaign has been created successfully',
      })

      if (callback) {
        yield call(callback)
      }
    }
  } catch (error) {
    yield put(actions.createArcCampaignFailure())
    notification.error({
      message: 'Error creating campaign',
      description: error.message || 'Failed to create ARC campaign',
    })
  } finally {
    yield put(actions.finishSaving())
  }
}

function* updateArcCampaignWorker({ payload }) {
  try {
    yield put(actions.startSaving())

    const { id, data, callback } = payload
    const formattedData = formatArcCampaignForAPI(data)
    formattedData.data.id = id
    const response = yield call(api.updateArcCampaign, { id, data: formattedData })

    if (response && response.data) {
      yield put(actions.updateArcCampaignSuccess({ data: response.data.data }))

      notification.success({
        message: 'Campaign updated',
        description: 'ARC campaign has been updated successfully',
      })

      if (callback) {
        yield call(callback)
      }
    }
  } catch (error) {
    yield put(actions.updateArcCampaignFailure())
    notification.error({
      message: 'Error updating campaign',
      description: error.message || 'Failed to update ARC campaign',
    })
  } finally {
    yield put(actions.finishSaving())
  }
}

function* deleteCampaignWorker({ payload }) {
  try {
    const { id, callback } = payload

    yield call(api.deleteArcCampaign, id)
    yield put(actions.deleteCampaignSuccess({ id }))

    notification.success({
      message: 'Campaign deleted',
      description: 'ARC campaign has been deleted successfully',
    })

    if (callback) {
      yield call(callback)
    }
  } catch (error) {
    yield put(actions.deleteCampaignFailure())
    notification.error({
      message: 'Error deleting campaign',
      description: error.message || 'Failed to delete ARC campaign',
    })
  }
}

function* resetFiltersAndSorterWorker() {
  yield put(actions.saveFiltersAndSorter({ search: '', filters: {}, sorter: {} }))
  yield put(actions.fetchArcCampaigns({ page: 1 }))
}

export default function* arcCampaignsSaga() {
  yield all([
    takeEvery(actions.fetchArcCampaigns.type, fetchArcCampaignsWorker),
    takeEvery(actions.fetchArcCampaignDetails.type, fetchArcCampaignDetailsWorker),
    takeEvery(actions.createArcCampaign.type, createArcCampaignWorker),
    takeEvery(actions.updateArcCampaign.type, updateArcCampaignWorker),
    takeEvery(actions.deleteCampaign.type, deleteCampaignWorker),
    takeEvery(actions.resetFiltersAndSorter.type, resetFiltersAndSorterWorker),
  ])
}